import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userProfileExists } from '../firebase';
import { useSelectedPlayers } from '../contexts/SelectedPlayersContext';
import {
  createPlayer,
  getPlayers,
  updatePlayerDetails,
  deletePlayer,
} from '../firebase';
import { getCurrentLevel } from '../utils/achievements';
import { Plus } from 'lucide-react';
import Input from './Input';
import Button from './Button';
import SimpleModal from './SimpleModal';
import PlayerModal from './PlayerModal';
import './PlayerManagement.css';

function PlayerManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedPlayerIds, setSelectedPlayers, updateAllPlayersData } =
    useSelectedPlayers();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [deletePin, setDeletePin] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    const guard = async () => {
      if (!user) return;
      const exists = await userProfileExists(user.uid);
      if (!exists) {
        navigate('/onboarding');
        return;
      }
      await loadPlayers();
    };
    guard();
  }, [user, navigate]);

  const loadPlayers = async () => {
    try {
      const playersList = await getPlayers(user.uid);
      setPlayers(playersList);
      updateAllPlayersData(playersList); // Update context with loaded data
    } catch (error) {
      console.error('Error loading players:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitCreatePlayer = async ({ name, age, emoji }) => {
    setCreating(true);
    try {
      await createPlayer(user.uid, name, emoji, age);
      setShowCreateForm(false);
      await loadPlayers();
    } catch (error) {
      console.error('Error creating player:', error);
      alert('Failed to create player. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handlePlayerToggle = (playerId) => {
    setSelectedPlayers((prev) => {
      const newSelection = prev.includes(playerId)
        ? prev.filter((id) => id !== playerId)
        : [...prev, playerId];

      return newSelection;
    });
  };

  const handleStartWorkout = () => {
    if (selectedPlayerIds.length > 0) {
      // Determine navigation based on current route
      if (location.pathname.startsWith('/workouts')) {
        navigate('/workouts/sports');
      } else if (location.pathname.startsWith('/skills')) {
        navigate('/skills/sports');
      } else {
        navigate('/sports');
      }
    }
  };

  const handleSelectAll = () => {
    if (selectedPlayerIds.length === players.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(players.map((player) => player.id));
    }
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
  };

  const submitUpdatePlayer = async ({ name, age, emoji }) => {
    if (!editingPlayer) return;
    setCreating(true);
    try {
      await updatePlayerDetails(user.uid, editingPlayer.id, name, emoji, age);
      setEditingPlayer(null);
      await loadPlayers();
    } catch (error) {
      console.error('Error updating player:', error);
      alert('Failed to update player. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPlayer(null);
  };

  const handleDeletePlayer = (player) => {
    setPlayerToDelete(player);
    setDeletePin('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletePin || deletePin.length !== 4) {
      setDeleteError('Please enter a 4-digit PIN');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      await deletePlayer(user.uid, playerToDelete.id, deletePin);

      // Remove from selected players if they were selected
      if (selectedPlayerIds.includes(playerToDelete.id)) {
        setSelectedPlayers((prev) =>
          prev.filter((id) => id !== playerToDelete.id)
        );
      }

      setShowDeleteModal(false);
      setPlayerToDelete(null);
      setDeletePin('');
      await loadPlayers(); // Reload players list
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete player');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setPlayerToDelete(null);
    setDeletePin('');
    setDeleteError('');
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setDeletePin(value);
    setDeleteError('');
  };

  if (loading) {
    return (
      <div className='player-management'>
        <div className='loading'>Loading players...</div>
      </div>
    );
  }

  return (
    <div className='sport-selection'>
      <h1>
        {players.length === 0
          ? 'Add Players'
          : location.pathname.startsWith('/workouts')
          ? 'Strength Workouts'
          : location.pathname.startsWith('/skills')
          ? 'Skills Training'
          : 'Select Players'}
      </h1>
      <h3>Who's playing today?</h3>

      {/* Select All Button */}
      {players.length > 0 && (
        <div className='select-all-section'>
          <Button
            type={
              selectedPlayerIds.length === players.length
                ? 'danger'
                : 'secondary'
            }
            onClick={handleSelectAll}
          >
            {selectedPlayerIds.length === players.length
              ? 'Remove All'
              : 'Select All'}
          </Button>
          {/* Start Button */}
          {players.length > 0 && selectedPlayerIds.length > 0 && (
            <Button type='primary' onClick={handleStartWorkout}>
              {location.pathname.startsWith('/workouts')
                ? 'Start Workout'
                : 'Start Skills Training'}{' '}
              with {selectedPlayerIds.length} Player
              {selectedPlayerIds.length > 1 ? 's' : ''}
            </Button>
          )}
        </div>
      )}

      <div className='sport-grid'>
        {/* Add New Player Card */}
        <div
          className={`sport-card ${showCreateForm ? 'creating' : ''}`}
          onClick={() => setShowCreateForm(true)}
        >
          <div className='sport-icon'>
            <Plus size={24} />
          </div>
          <h3>Add New Player</h3>
          <p>Create a new player profile</p>
        </div>

        {/* Create Player Form */}
        {showCreateForm && (
          <PlayerModal
            isOpen={showCreateForm}
            mode='create'
            isSubmitting={creating}
            onClose={() => setShowCreateForm(false)}
            onSubmit={submitCreatePlayer}
          />
        )}

        {/* Edit Player Form */}
        {editingPlayer && (
          <PlayerModal
            isOpen={Boolean(editingPlayer)}
            mode='edit'
            isSubmitting={creating}
            initialName={editingPlayer?.playerName}
            initialAge={editingPlayer?.age}
            initialEmoji={editingPlayer?.avatar || '🐶'}
            onClose={handleCancelEdit}
            onSubmit={submitUpdatePlayer}
            deleteAction={{
              text: 'Delete',
              onClick: () => {
                setEditingPlayer(null);
                handleDeletePlayer(editingPlayer);
              },
              disabled: creating,
            }}
          />
        )}

        {/* Existing Players */}
        {players.map((player) => (
          <div
            key={player.id}
            className={`sport-card ${
              selectedPlayerIds.includes(player.id) ? 'selected' : ''
            }`}
          >
            <div className='sport-icon'>{player.avatar || '🐶'}</div>
            <h3>{player.playerName}</h3>
            <div className='player-stats'>
              <span>Points: {player.totalScore || 0}</span>
              <span>Streak: {player.streakDays || 0} days</span>
            </div>
            {getCurrentLevel(player.totalScore || 0) && (
              <div className='current-level'>
                {getCurrentLevel(player.totalScore || 0).emoji}{' '}
                {getCurrentLevel(player.totalScore || 0).name}
              </div>
            )}
            {selectedPlayerIds.includes(player.id) && (
              <div className='selected-indicator'>✓ Selected</div>
            )}
            <div className='player-actions'>
              <Button
                type='tertiary'
                size='small'
                onClick={() => handleEditPlayer(player)}
              >
                Edit
              </Button>
              <Button
                type={
                  selectedPlayerIds.includes(player.id) ? 'danger' : 'secondary'
                }
                size='small'
                onClick={() => handlePlayerToggle(player.id)}
              >
                {selectedPlayerIds.includes(player.id) ? 'Remove' : 'Select'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Player Modal */}
      <SimpleModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        title={`Delete ${playerToDelete?.playerName}`}
        type='danger'
        message={`Are you sure you want to delete ${playerToDelete?.playerName}?

This will permanently remove:
• All their points (${playerToDelete?.totalScore || 0} points)
• Their current streak (${playerToDelete?.streakDays || 0} days)
• All workout badges and achievements
• Their complete workout history

This action cannot be undone.`}
        actions={[
          {
            text: 'Cancel',
            type: 'secondary',
            onClick: handleCancelDelete,
          },
          {
            text: isDeleting ? 'Deleting...' : 'Delete Player',
            type: 'danger',
            onClick: handleConfirmDelete,
            disabled: isDeleting || deletePin.length !== 4,
          },
        ]}
      >
        <div className='pin-input-section'>
          <label>Enter Parent PIN:</label>
          <Input
            type='tel'
            value={deletePin}
            onChange={handlePinChange}
            placeholder='0000'
            maxLength={4}
            pattern='[0-9]*'
            inputMode='numeric'
            autoComplete='off'
            autoCorrect='off'
            autoCapitalize='off'
            spellCheck='false'
            disabled={isDeleting}
          />
          {deleteError && <div className='error-message'>{deleteError}</div>}
        </div>
      </SimpleModal>
    </div>
  );
}

export default PlayerManagement;
